/**
 * Danish message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: informal du (locked in l10n/locales.md).
 *
 * Platform terms follow Danish UI conventions (Tema for theme, samtale
 * for conversation, parre for pair, afvise for dismiss, modtager for a
 * receiving surface); "Glance" is the untranslated feature brand name
 * (glossary D1) and "klip" is a locked loanword (D2).
 *
 * Plural notes: Danish is a {one, other} CLDR locale, and its count
 * nouns mostly inflect (enhed → enheder, resultat → resultater) while a
 * few stay invariant (tegn → tegn, opkald → opkald), so every
 * count-bearing function selects through `selectPlural("da", …)`.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Forbundet",
    statusReconnecting: "Genopretter forbindelse…",
    statusDisconnected: "Afbrudt",
    openSettings: "Åbn indstillinger",
    pairedFallbackDevice: "din telefon",
    publicModeToggleEnable: "Skjul notifikationsindhold",
    publicModeToggleDisable: "Vis notifikationsindhold",
  },

  notifications: {
    replyAriaLabel: "Svar",
    replyPlaceholder: "Svar…",
    replySend: "Send",
    sent: "Sendt",
    failedToSend: "Kunne ikke sende",
    unread: "Ulæst",
    dismiss: "Afvis",
    notificationImageAlt: "Notifikationsbillede",
  },

  conversations: {
    replyAriaLabel: "Svar",
    replyPlaceholder: "Svar…",
    replySend: "Send",
    sent: "Sendt",
    failedToSend: "Kunne ikke sende",
    dismiss: "Afvis samtale",
    messagesListLabel: "Beskeder i denne samtale",
    deviceOwner: "Dig",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Ingen ulæste beskeder"
        : `${count} ${selectPlural("da", count, {
            one: "ulæst besked",
            other: "ulæste beskeder",
          })}`,
  },

  list: {
    today: "I dag",
    yesterday: "I går",
    searchNotifications: "Søg i notifikationer",
    clearSearch: "Ryd søgning",
    clearAll: "Ryd alle",
    markAllRead: "Markér alle som læst",
    groupCheckboxLabel: "Gruppér",
    noNotificationsTitle: "Endnu ingen notifikationer",
    noNotificationsDesc: "Nye beskeder, der spejles fra din telefon, vises her.",
    noMatchesTitle: "Ingen resultater",
    notificationsList: "Notifikationer",
    privacyAppList: "Notifikationer efter app",
    clear: "Ryd",
    clearMatches: (count: number): string =>
      `Ryd ${count} ${selectPlural("da", count, { one: "resultat", other: "resultater" })}`,
    noMatchesDesc: (query: string): string => `Ingen notifikationer matcher ”${query}”.`,
    appNotificationsGroup: (appName: string): string => `Notifikationer fra ${appName}`,
    dismissAllFrom: (appName: string): string => `Afvis alle fra ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("da", count, { one: "besked", other: "beskeder" })
          : kind === "calls"
            ? selectPlural("da", count, { one: "opkald", other: "opkald" })
            : selectPlural("da", count, { one: "notifikation", other: "notifikationer" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "afvis",
    kbdNavigate: "naviger",
    kbdOrSeparator: "eller",
  },

  settings: {
    back: "Tilbage til notifikationer",
    settings: "Indstillinger",
    general: "Generelt",
    theme: "Tema",
    themeAuto: "Automatisk",
    themeLight: "Lyst",
    themeDark: "Mørkt",
    language: "Sprog",
    languageSystemDefault: "Systemstandard",
    notificationsSection: "Notifikationer",
    osNotifications: "Systemnotifikationer",
    callNotifications: "Opkaldsnotifikationer",
    privacySecurity: "Privatliv",
    analyticsToggle: "Anonym brugsstatistik",
    analyticsToggleDescription: "Kun antal og milepæle — aldrig dine notifikationer.",
    statusConnected: "Forbundet",
    statusReconnecting: "Genopretter forbindelse…",
    statusDisconnected: "Afbrudt",
    disconnectFromPhone: "Afbryd forbindelsen til telefonen",
    disconnectConfirm: "Er du sikker? Dette ophæver parringen af din enhed.",
    yesDisconnect: "Ja, afbryd",
    cancel: "Annuller",
    version: "Version",
    encryption: "Kryptering",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Brug for hjælp? Skriv til __EMAIL__",
    disconnectFailed: "Kunne ikke afbryde forbindelsen — prøv igen",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Forbindelsen blev afbrudt, men lokal oprydning mislykkedes: ${errorMsg}. Genindlæs siden.`,
    disconnectLocalCleanupFailed:
      "Forbindelsen blev afbrudt, men lokal oprydning mislykkedes. Genindlæs siden.",
  },

  pairing: {
    enterCodeInstruction: "Indtast denne kode i Reflecto-appen",
    pairedSuccessfully: "Parring gennemført!",
    pairingFailed: "Parring mislykkedes",
    requestingPairingCode: "Anmoder om parringskode…",
    codeExpired: "Kode udløbet",
    tryAgain: "Prøv igen",
    failedToStartPairing: "Kunne ikke starte parring",
    installHeading: "Har du ikke appen endnu?",
    installScanHint: "Scan med telefonens kamera",
    installQrLabel: "QR-kode til at installere Reflecto på din telefon",
    capabilitiesHeading: "Efter parring kan du:",
    capabilityMirror: "Se telefonens notifikationer her",
    capabilityReply: "Svare uden at tage telefonen op",
    capabilityShare: "Dele udklipsholder og filer begge veje",
    encryptionNote: "End-to-end-krypteret. Serveren ser aldrig dit indhold.",
  },

  dnd: {
    title: "Forstyr ikke",
    description: "Sæt notifikationer på denne enhed på pause den næste time.",
    snooze1h: "Sæt på pause i 1 time",
    snoozeActiveUntil: (formattedTime: string): string => `Aktiv indtil ${formattedTime}`,
    cancelSnooze: "Annuller",
  },

  connectors: {
    title: "Forbundne tjenester",
    emptyBody:
      "Endnu ingen forbundne tjenester. Forbind ChatGPT eller Claude fra deres connector-indstillinger, og godkend derefter koden på din telefon.",
    scopeSend: "Kan sende notifikationer",
    added: (date: string): string => `Tilføjet ${date}`,
    lastUsed: (relative: string): string => `Sidst brugt ${relative}`,
    neverUsed: "Aldrig brugt",
    revoke: "Afbryd",
    revokeConfirm: (label: string): string => `Afbryd ${label}? Adgangen ophører øjeblikkeligt.`,
    revokeAction: "Afbryd",
    revokeConfirmLabel: (label: string): string => `Afbryd ${label}`,
    cancel: "Annuller",
    loadError: "Kunne ikke indlæse forbundne tjenester. Prøv igen.",
    revokeError: "Kunne ikke afbryde tjenesten. Prøv igen.",
    retry: "Prøv igen",
  },

  safetyNumber: {
    rowLabel: "Bekræft kryptering",
    rowSublabel: "Sammenlign sikkerhedstal med din telefon",
    cardTitle: "Sikkerhedsnummer",
    caption: (deviceLabel: string): string =>
      `Sammenlign med tallet vist på ${deviceLabel}. Hvis begge matcher, er din end-to-end-kryptering intakt.`,
    repairNote: "Dette tal ændres, når du sætter en enhed op igen.",
    copy: "Kopier",
    copied: "Kopieret",
    maskedHint: "Skjult mens privatlivsbeskyttelsestilstand er til",
  },

  feedback: {
    title: "Send feedback",
    close: "Luk",
    messageLabel: "Din besked",
    messagePlaceholder:
      "Fandt du en fejl, har du en idé, eller føles noget forkert? Fortæl os det.",
    categoryLabel: "Kategori",
    categoryBug: "Fejl",
    categoryIdea: "Idé",
    categoryOther: "Andet",
    emailLabel: "E-mail (valgfrit, kun hvis du vil have et svar)",
    emailPlaceholder: "dig@eksempel.dk",
    emailHelp: "Vi tilføjer dig ikke til nogen liste.",
    disclosure:
      "Din besked sendes til Reflectos server og registreres i vores interne issue-tracker. Vi vedhæfter din app-version og et enheds-ID, der tildeles under parring, så vi kan sammenholde rapporter. Vi ser ikke dine notifikationer, kontakter eller andre personlige data. E-mail er valgfrit — angiv den kun, hvis du vil have et svar. Vi tilføjer dig ikke til nogen liste.",
    submit: "Send feedback",
    sending: "Sender…",
    success: "Tak — læses af et menneske, som regel inden for 24 timer",
    error: "Kunne ikke sende. Prøv igen.",
    done: "Færdig",
    sendFeedback: "Send feedback",
  },

  uninstall: {
    headline: "Reflectos udvidelse er afinstalleret",
    body: "Der har aldrig været en konto, og udvidelsen gemte din parring på denne computer, så der er ikke noget tilbage, du skal rydde op i.",
    ask: "Har du tredive sekunder, vil vi gerne vide, hvad der gik galt.",
    categoryLegend: "Hvilken slags feedback er det?",
    categoryBug: "Noget gik i stykker",
    categoryIdea: "Der manglede noget, jeg havde brug for",
    categoryOther: "Noget andet",
    messageLabel: "Hvad gik galt?",
    messagePlaceholder: "Notifikationer holdt op med at komme efter et par timer.",
    messageRequired: "Skriv en sætning først, og send så.",
    charactersLeft: (count: number): string => `${count} tegn tilbage`,
    emailLabel: "E-mail (valgfrit)",
    emailPlaceholder: "dig@eksempel.dk",
    emailHelp: "Bruges kun til at svare dig. Intet andet.",
    submit: "Send feedback",
    sending: "Sender…",
    retry: "Prøv igen",
    privacyNote:
      "Denne formular er ikke knyttet til nogen enhed. Den indeholder ingen af dine notifikationer, din udklipsholder eller dine filer. E-mail er valgfrit og bruges kun til at svare.",
    success: "Tak. Et menneske læser hver eneste af dem.",
    errorGeneric: "Det blev ikke sendt. Din besked er her stadig.",
    errorRateLimited:
      "Du har sendt flere beskeder for nylig. Prøv igen om cirka en time. Din tekst bliver stående.",
    footerWebApp: "Reflectos webapp virker stadig uden udvidelsen.",
    footerWebAppLink: "Åbn webappen",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Luk",
    aiSummaryTag: "AI-resumé",
    errorAi: "Kunne ikke lave et resumé lige nu.",
    retryCta: "Prøv igen",
    downloadHeading: "Få AI-resuméer",
    downloadBody:
      "Bliv opdateret på det, du gik glip af, med et AI-resumé på selve enheden. Kører udelukkende på din enhed.",
    downloadCta: "Hent model",
    downloadPrivacyNote: "En engangshentning. Serveren ser aldrig dine notifikationer.",
    downloadingHeading: "Henter model…",
    caughtUp: "Du er helt opdateret — der er intet, der kræver din opmærksomhed lige nu.",
    needsYou: "Kræver opmærksomhed",
    quiet: "Øvrige",
    scanning: (pending: number): string => `Opsummerer ${pending}…`,
    jump: "Åbn notifikation",
    affordanceReply: "Svar",
    affordanceOpen: "Åbn",
    affordanceCopyOtp: "Kopiér kode",
    buttonLabel: "Glance",
  },

  update: {
    title: "Opdatering tilgængelig",
    description: "En ny version af Reflecto er klar. Genindlæs for at opdatere.",
    reload: "Genindlæs",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Vi kaldte denne enhed ”${name}”.`,
    cardLooksGood: "Ser godt ud",
    cardEdit: "Rediger",
    cardAriaLabel: "Bekræft enhedsnavn",
    renameTitle: "Omdøb denne enhed",
    renameLabel: "Enhedsnavn",
    renameSave: "Gem",
    renameCancel: "Annuller",
    renameFailed: "Kunne ikke omdøbe. Prøv igen.",
  },

  collision: {
    title: (name: string): string => `Du har allerede en ”${name}”`,
    subtitle: "Hvad skal vi kalde denne?",
    chipWork: (name: string): string => `Arbejde ${name}`,
    chipHome: (name: string): string => `Hjemme ${name}`,
    chipPersonal: (name: string): string => `Privat ${name}`,
    customLabel: "Brugerdefineret navn",
    save: "Gem",
    useAnyway: "Brug alligevel det oprindelige navn",
    saveFailed: "Kunne ikke gemme det nye navn. Prøv igen.",
    ariaLabel: "Løs dobbelt enhedsnavn",
  },

  clipboard: {
    title: "Udklipsholder",
    emptyTitle: "Ingen klip endnu",
    empty: "Klip fra dine andre enheder vises her. Tryk for at kopiere.",
    from: (label: string): string => `fra ${label}`,
    to: (label: string): string => `til ${label}`,
    copy: "Kopiér til udklipsholder",
    copied: "Kopieret",
    reveal: "Vis",
    hide: "Skjul",
    maskedHint: "Indhold skjult — tryk på Vis for at se det",
    sensitiveChip: "Følsom",
    cloudChip: "fra dit API-token",
    delete: "Slet klip",
    sendPlaceholder: "Indsæt tekst, der skal sendes…",
    sendCta: "Send",
    sendSensitiveToggle: "Markér som følsom",
    sendSuccess: (n: number): string =>
      `Sendt til ${n} ${selectPlural("da", n, { one: "enhed", other: "enheder" })}.`,
    sendPartial: (n: number, total: number): string =>
      `Nåede ${n}/${total} ${selectPlural("da", total, { one: "enhed", other: "enheder" })}.`,
    sendNoDevices: "Ingen parrede enheder at sende til.",
    sendFailed: "Kunne ikke sende. Prøv igen.",
    sendEmpty: "Intet at sende.",
    dockRecentHeading: "Seneste",
    dockEmptyClipboard: "Din udklipsholder er tom.",
    clearAll: "Ryd alle",
    clearAllConfirm: "Ja, ryd alle",
    clearAllCancel: "Annuller",
    shareTitle: "Send til dine enheder",
    shareReview: "Gennemse det, du sender. Det går til dine parrede enheder, end-to-end-krypteret.",
    shareSend: "Send",
    shareCancel: "Annuller",
    shareNotPaired: "Par en enhed først for at sende delt tekst.",
    shareOpenApp: "Åbn Reflecto",
    pasteFromClipboard: "Indsæt fra udklipsholder",
    readFailed: "Kunne ikke læse din udklipsholder — indsæt nedenfor i stedet.",
  },
  install: {
    title: "Installer Reflecto",
    body: "Føj til din startskærm for hurtigere adgang.",
    cta: "Installer",
    dismissAria: "Afvis installationsprompt",
  },
  nav: {
    home: "Hjem",
    clipboard: "Udklipsholder",
    files: "Filer",
    ariaLabel: "Hovedmenu",
    fileHiddenPublicMode: "Skjult, mens privatlivstilstand er slået til.",
  },
  common: {
    loading: "Indlæser",
  },
  errors: {
    notPaired: "Ikke parret",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} på ${os}`,
    webFallback: "Webbrowser",
  },

  palette: {
    placeholder: "Skriv en kommando…",
    noResults: "Ingen matchende kommandoer",
    ariaLabel: "Kommandopalet",
    clearAll: "Ryd alle notifikationer",
    glance: "Glance-resumé",
    cancelSnooze: "Annuller pause",
    copyClip: "Kopiér seneste modtagne klip",
  },

  fileTransfer: {
    sendTitle: "Send en fil",
    sendChooseFile: "Vælg fil",
    sendDeviceLabel: "Send til",
    sendPhoneFallbackName: "Telefon",
    send: "Send fil",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "Forbereder…" : `Forbereder — ${pct}%`,
    sendDelivering: "Leverer til din telefon…",
    sendWaitingAccept: "Venter på, at din telefon accepterer…",
    sendWaitingAppOpen: "Åbn Reflecto på din telefon for at acceptere",
    sendWaitingAppOpenHint:
      "Din telefon kan ikke modtage push-notifikationer og viser først denne fil, når Reflecto er åben.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `Uploader del ${partNumber} af ${partCount}…`,
    sendDone: "Sendt.",
    sendFailedRejected: "Din telefon afviste filen.",
    sendFailedNoAnswer: "Intet svar fra din telefon.",
    sendFailedUploadFailed: "Upload mislykkedes. Prøv igen.",
    sendFailedCancelled: "Annulleret.",
    sendFailedGeneric: "Noget gik galt. Prøv igen.",
    sendTooLarge: (maxMiB: number): string => `Filen er for stor (maks. ${maxMiB} MiB).`,
    sendKeepOpenHint: "Hold denne fane åben, indtil overførslen er færdig.",
    sendAnother: "Send en til",
    sendNoDevice: "Par en telefon først for at sende filer.",
    sendCancel: "Annuller",
    sendPeerProgress: (chunksDone: number): string =>
      `Telefonen har modtaget ${chunksDone} blokke.`,

    receiveTitle: "Indgående filer",
    receiveOfferFrom: (sender: string): string => `${sender} vil sende dig en fil`,
    receiveAccept: "Accepter",
    receiveDecline: "Afvis",
    receiveReceiving: (n: number, total: number): string => `Modtager del ${n} af ${total}…`,
    receiveDone: "Gemt.",
    receiveFailed: "Overførslen mislykkedes.",
    receiveCancelled: "Annulleret.",
    receiveCancel: "Annuller",

    errorChunkAuthFail: "Overførslen mislykkedes — dataene kunne ikke verificeres. Prøv igen.",
    errorHashMismatch: "Overførslen mislykkedes — filen bestod ikke integritetstjekket. Prøv igen.",
    errorStorageFull: "Ikke nok lagerplads til denne overførsel.",
    errorExpired: "Overførslen er udløbet.",
    errorQuotaExceeded: "Den daglige overførselsgrænse er nået — prøv igen i morgen.",
    errorInternal: "Overførslen mislykkedes — noget gik galt.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct} %`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "Afvis",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "Overførselsstatus",
    outgoing: (n: number): string => `Udgående (${n})`,
    incoming: (n: number): string => `Indgående (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "Accepter alle",
    dropTitle: "Træk og slip her",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `Filer op til ${size}.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "Træk og slip her, eller vælg en fil at sende",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "Direkte",
    transportDirectHint: "Sendt direkte mellem enheder — aldrig gemt på vores servere.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "Via server",
    transportRelayHint:
      "Videresendt via vores servere, stadig end-to-end-krypteret — vi kan ikke læse det.",
    directBadge: "Direkte",
  },
  time: {
    justNow: "lige nu",
    minutesAgo: (count: number): string => `${count} min. siden`,
    hoursAgo: (count: number): string => `${count} t. siden`,
  },
} satisfies MessagesShape;
